import { nextTick } from "vue"

const LOAD_MORE_DEBOUNCE_MS = 100

/**
 * @param {import('vue').Ref | { value?: { getDropdownMenuElement?: () => HTMLElement | null } | null }} customSelectRef
 * @returns {HTMLElement | null}
 */
export function getLazySelectDropdownMenu(customSelectRef) {
  return customSelectRef?.value?.getDropdownMenuElement?.() ?? null
}

/**
 * vue-select resets dropdown scroll when options change; restore after append.
 * @param {import('vue').Ref | { value?: { getDropdownMenuElement?: () => HTMLElement | null } | null }} customSelectRef
 * @param {number} scrollTop
 */
export async function restoreLazySelectDropdownScroll(
  customSelectRef,
  scrollTop
) {
  if (typeof scrollTop !== "number") return

  await nextTick()
  requestAnimationFrame(() => {
    const menu = getLazySelectDropdownMenu(customSelectRef)
    if (menu) {
      menu.scrollTop = scrollTop
    }
  })
}

/**
 * @param {Array<{ value?: unknown }>} existingOptions
 * @param {Array<{ value?: unknown }>} nextOptions
 * @returns {Array<{ value?: unknown }>}
 */
export function dedupeLazySelectOptions(existingOptions, nextOptions) {
  const existingIds = new Set(
    (existingOptions || [])
      .map((option) => String(option?.value ?? ""))
      .filter(Boolean)
  )

  return (nextOptions || []).filter((option) => {
    const id = String(option?.value ?? "")
    return id && !existingIds.has(id)
  })
}

/**
 * Debounced infinite-scroll loader (mirrors LazyLoadSelect).
 * @param {{
 *   isBlocked?: () => boolean,
 *   onLoad: () => void,
 *   getTimeoutId: () => number | null,
 *   setTimeoutId: (id: number | null) => void
 * }} args
 */
export function scheduleLazySelectLoadMore({
  isBlocked,
  onLoad,
  getTimeoutId,
  setTimeoutId
}) {
  if (isBlocked?.()) return

  const existingTimeoutId = getTimeoutId()
  if (existingTimeoutId) {
    clearTimeout(existingTimeoutId)
  }

  const timeoutId = window.setTimeout(() => {
    setTimeoutId(null)
    if (isBlocked?.()) return
    onLoad()
  }, LOAD_MORE_DEBOUNCE_MS)

  setTimeoutId(timeoutId)
}
